import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { createZodResolver } from '../forms/validation';
import { TextField } from '../forms';
import { createSupplier, ApiError, type Supplier } from '../../lib/apiClient';
import '@tylertech/forge/dialog';
import '@tylertech/forge/button';
import './CreateSupplierDialog.css';

const createSupplierFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(200),
  description: z.string().max(2000).optional(),
  websiteUrl: z.string().url('Must be a valid URL').max(500).optional().or(z.literal('')),
});
type CreateSupplierFormData = z.infer<typeof createSupplierFormSchema>;

interface CreateSupplierDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (supplier: Supplier) => void;
}

const DialogElement = 'forge-dialog' as React.ElementType;
const ForgeButton = 'forge-button' as React.ElementType;

export function CreateSupplierDialog({
  open,
  onClose,
  onCreated,
}: CreateSupplierDialogProps): JSX.Element | null {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateSupplierFormData>({
    resolver: createZodResolver(createSupplierFormSchema),
  });

  const handleClose = (): void => {
    reset();
    setError(null);
    onClose();
  };

  const onSubmit = async (data: CreateSupplierFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);
    try {
      const supplier = await createSupplier({
        name: data.name,
        description: data.description || undefined,
        websiteUrl: data.websiteUrl || undefined,
      });
      reset();
      onCreated(supplier);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create supplier');
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <DialogElement open={open} onDismiss={handleClose} aria-labelledby="create-supplier-title">
      <div className="create-supplier-dialog">
        <h2
          id="create-supplier-title"
          className="forge-typography--headline6 create-supplier-dialog__title"
        >
          Create Supplier
        </h2>
        {error && <p className="create-supplier-dialog__error">{error}</p>}
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField label="Supplier Name" required error={errors.name} {...register('name')} />
          <TextField label="Description" error={errors.description} {...register('description')} />
          <TextField label="Website URL" error={errors.websiteUrl} {...register('websiteUrl')} />
          <div className="create-supplier-dialog__actions">
            <ForgeButton
              type="button"
              variant="outlined"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </ForgeButton>
            <ForgeButton type="submit" variant="raised" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Supplier'}
            </ForgeButton>
          </div>
        </form>
      </div>
    </DialogElement>
  );
}
